import React, { useState, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./App.css";
import SiteNav from "./SiteNav";

gsap.registerPlugin(ScrollTrigger);

export default function Contact() {
  const root = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    order: "",
    topic: "General",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const checkRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.set([".contact-hero .hero-kicker", ".contact-hero .hero-sub"], {
        y: 28,
        opacity: 0,
      });
      gsap.from(".contact-hero .hero-title", {
        opacity: 0,
        y: 18,
        duration: 0.9,
        ease: "power3.out",
      });
      gsap.to(".contact-hero .hero-kicker", {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
        delay: 0.1,
      });
      gsap.to(".contact-hero .hero-sub", {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power3.out",
        delay: 0.25,
      });

      // Reveal-ups
      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          y: 40,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      });

      // Footer fade
      gsap.from(".site-footer a", {
        opacity: 0,
        y: 12,
        stagger: 0.08,
        duration: 0.6,
        ease: "power3.out",
        scrollTrigger: { trigger: ".site-footer", start: "top 85%" },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Tell us a bit about your issue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    // Fake submission (front-end only). Replace with your API later.
    setSent(true);

    // Animate the success check
    const path = checkRef.current;
    if (path) {
      const len = path.getTotalLength();
      gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 0.9,
        ease: "power2.out",
        delay: 0.15,
      });
      gsap.fromTo(
        ".success-card",
        { y: 16, autoAlpha: 0, scale: 0.98 },
        { y: 0, autoAlpha: 1, scale: 1, duration: 0.45, ease: "power3.out" }
      );
    }
  };

  return (
    <div ref={root} className="page">
      {/* If you already render a global nav, remove this header */}
      <SiteNav/>

      {/* Mobile overlay menu */}
      <nav id="mnav" className="mnav" data-open="false">
        <Link to="/" onClick={() => document.getElementById("mnav")?.setAttribute("data-open", "false")}>
          Home
        </Link>
        <Link to="/about" onClick={() => document.getElementById("mnav")?.setAttribute("data-open", "false")}>
          About
        </Link>
        <Link to="/payment" onClick={() => document.getElementById("mnav")?.setAttribute("data-open", "false")}>
          Payment
        </Link>
        <Link to="/contact" onClick={() => document.getElementById("mnav")?.setAttribute("data-open", "false")}>
          Contact
        </Link>
      </nav>

   {/* HERO — full screen + B/W background */}
    <section className="hero contact-hero v2 full" aria-label="Contact support">
      {/* Background layers */}
      <div className="h-photo" aria-hidden style={{ backgroundImage: 'url("/eDSCF0379.webp")' }} />
      <div className="h-aurora" aria-hidden>
        <span className="blob b1" />
        <span className="blob b2" />
      </div>
      <div className="h-beams" aria-hidden>
        <span className="beam a" />
        <span className="beam b" />
        <span className="beam c" />
      </div>
      <div className="h-grid" aria-hidden />
      <div className="h-noise" aria-hidden />
      <div className="h-overlay" aria-hidden />

      {/* Content */}
      <div className="h-inner">
        <p className="hero-kicker">Get In Touch</p>
        <h1 className="hero-title">We&apos;re Here To Help</h1>
        <p className="hero-sub">
          Have questions about our products? Need support with your order? We&apos;re just a message away.
        </p>
        <div className="hero-cta">
          <a className="cta-solid" href="mailto:support@dopeshot.example">Email support</a>
          <a className="cta-ghost" href="#contact-form">Open contact form</a>
        </div>
      </div>

    </section>


{/* QUICK CONTACT INFO (enhanced) */}
<section className="slice qci">
  <div className="container">
    <div className="qci-wrap">
      <header className="qci-head">
        <h3>Quick contact info</h3>
        <p>Fastest ways to reach us.</p>
      </header>

      <div className="qci-grid">
        {/* Phone */}
        <article className="qci-card">
          <div className="qci-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M22 16.92v2a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07A19.5 19.5 0 0 1 3.15 9.81 19.8 19.8 0 0 1 .08 1.18 2 2 0 0 1 2.06 0h2a2 2 0 0 1 2 1.72c.12.93.33 1.84.62 2.71a2 2 0 0 1-.45 2.11L5.1 7.67a16 16 0 0 0 6.23 6.23l1.13-1.13a2 2 0 0 1 2.11-.45c.87.29 1.78.5 2.71.62A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="qci-body">
            <h4>Phone</h4>
            <a className="qci-main" href="tel:+1-888-DOPESHOT">1-888-DOPESHOT</a>
            <p className="qci-sub">Contact us within US</p>
          </div>
        </article>

        {/* Email */}
        <article className="qci-card">
          <div className="qci-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="qci-body">
            <h4>Email</h4>
            <a className="qci-main" href="mailto:support@dopeshot.example">support@dopeshot.example</a>
            <p className="qci-sub">We usually reply within 1–2 business days</p>
          </div>
        </article>

        {/* Hours */}
        <article className="qci-card">
          <div className="qci-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="qci-body">
            <h4>Hours</h4>
            <div className="qci-main">Mon–Fri</div>
            <p className="qci-sub">9:00 AM – 5:00 PM (local)</p>
          </div>
        </article>

        {/* Order status 
        <article className="qci-card">
          <div className="qci-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M3 7h18M3 7l2 12h14l2-12M7 7l5-4 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="qci-body">
            <h4>Order status</h4>
            <div className="qci-main">Tracking via email</div>
            <p className="qci-sub">Look for the shipping confirmation</p>
          </div>
        </article>*/}
      </div>
    </div>
  </div>
</section>

      {/* CONTACT FORM — redesigned */}
<section id="contact-form" className="slice alt">
  <div className="container" style={{ maxWidth: "980px", margin: "0 auto" }}>
    <div className="cf-card spec-panel reveal-up">
      <header className="cf-head">
        <h3>Send us a message</h3>
        <p>We’ll reply within 1–2 business days.</p>
      </header>

      {!sent ? (
        <form onSubmit={onSubmit} noValidate className="cf-grid">
          {/* Name */}
          <div className="cf-field">
            <label htmlFor="name" className="cf-label">Name</label>
            <div className="cf-input-wrap">
              <span className="cf-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M20 22a8 8 0 1 0-16 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                id="name"
                type="text"
                className="cf-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoComplete="name"
                aria-invalid={!!errors.name}
              />
            </div>
            {errors.name && <div className="cf-error">{errors.name}</div>}
          </div>

          {/* Email */}
          <div className="cf-field">
            <label htmlFor="email" className="cf-label">Email</label>
            <div className="cf-input-wrap">
              <span className="cf-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <input
                id="email"
                type="email"
                className="cf-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                autoComplete="email"
                aria-invalid={!!errors.email}
              />
            </div>
            {errors.email && <div className="cf-error">{errors.email}</div>}
          </div>

          {/* Order # */}
          <div className="cf-field">
            <label htmlFor="order" className="cf-label">Order # (optional)</label>
            <div className="cf-input-wrap">
              <span className="cf-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M7 10h10M7 14h10M7 18h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                id="order"
                type="text"
                className="cf-input"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                autoComplete="off"
                placeholder="e.g. DS-12345"
              />
            </div>
          </div>

          {/* Topic */}
          <div className="cf-field">
            <label htmlFor="topic" className="cf-label">Topic</label>
            <div className="cf-input-wrap">
              <span className="cf-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              <select
                id="topic"
                className="cf-input cf-select"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
              >
                <option>General</option>
                <option>Order issue</option>
                <option>Product question</option>
                <option>Returns</option>
              </select>
            </div>
          </div>

          {/* Message (spans full width) */}
          <div className="cf-field cf-span2">
            <label htmlFor="message" className="cf-label">Message</label>
            <div className="cf-input-wrap">
              <span className="cf-icon" aria-hidden style={{alignSelf:"flex-start", marginTop: 12}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15a4 4 0 0 1-4 4H9l-6 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              </span>
              <textarea
                id="message"
                rows="5"
                className="cf-input cf-textarea"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                aria-invalid={!!errors.message}
              />
            </div>
            {errors.message && <div className="cf-error">{errors.message}</div>}
          </div>

          {/* Actions */}
          <div className="cf-actions cf-span2">
            <button type="submit" className="cf-submit">Send message</button>
            <a className="cf-alt" href="mailto:support@dopeshot.example">Email instead</a>
          </div>
        </form>
      ) : (
        <div className="success-card">
          <svg width="70" height="70" viewBox="0 0 52 52">
            <path
              ref={checkRef}
              d="M14 27 L22 35 L38 17"
              fill="none"
              stroke="#fff"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h4>Message sent successfully!</h4>
          <p>Thank you for reaching out. We’ll respond within 1–2 business days.</p>
        </div>
      )}
    </div>
  </div>
</section>


      {/* CTA */}
      <section className="slice">
        <div className="container cta">
          <div>
            <h3 className="reveal-up">Need help right now?</h3>
            <p className="reveal-up">Shoot us an email and we’ll jump on it.</p>
          </div>
          <a href="mailto:support@dopeshot.example" className="cta-btn">Email support</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        {/* <div className="foot-inner">
          <Link to="/">Home</Link>
          <Link to="/about">About</Link>
          <Link to="/payment">Payment</Link>
          <Link to="/contact">Contact</Link>
        </div>*/}
        <div className="foot-copy">© Dope Shot</div>
      </footer>
    </div>
  );
}

/* — helpers — */
function FieldError({ children }) {
  return (
    <div style={{ color: "#ffb3b3", fontSize: 13, marginTop: 6 }}>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: 12,
  border: "1px solid var(--line)",
  background: "#0a0a0a",
  color: "var(--fg)",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  fontSize: "16px",
  '&:focus': {
    borderColor: "#fff",
    boxShadow: "0 0 0 2px rgba(255,255,255,0.1)"
  }
};
